// src/components/dashboard/CustomerManager.js - CRITICAL FIX for localeCompare Error
import {
  Calendar,
  DollarSign,
  Download,
  Edit,
  Eye,
  Filter,
  Mail,
  MessageCircle,
  Phone,
  Search,
  TrendingUp,
  Users,
  X
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useBookings } from '../../hooks/useBookings';

const CustomerManager = () => {
  const { user } = useAuth();
  const { bookings } = useBookings();
  
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [filterBy, setFilterBy] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState(null);

  // Load customers from bookings data
  useEffect(() => {
    if (bookings.length > 0) {
      processCustomersFromBookings();
    }
  }, [bookings]);

  // Filter customers when search or filters change
  useEffect(() => {
    filterCustomers();
  }, [customers, searchTerm, sortBy, filterBy]);

  const processCustomersFromBookings = () => {
    try {
      setLoading(true);
      
      // Group bookings by customer
      const customerMap = new Map();
      
      bookings.forEach(booking => {
        // FIXED: Handle multiple customer ID field variations
        const customerId = booking.customerId || booking.customer_id || booking.id;
        
        // FIXED: Safe customer name extraction with fallbacks
        const customerName = booking.customerName || booking.customer_name || booking.name || '';
        const customerPhone = booking.customerPhone || booking.customer_phone || booking.phone || '';
        const customerEmail = booking.customerEmail || booking.customer_email || booking.email || '';
        const customerWhatsapp = booking.customerWhatsapp || booking.customer_whatsapp || customerPhone;
        
        // FIXED: Skip bookings without valid customer identification
        if (!customerId && !customerName && !customerPhone) {
          console.warn('Skipping booking with insufficient customer data:', booking);
          return;
        }
        
        // Use a composite key if customerId is missing
        const effectiveCustomerId = customerId || `${customerName}_${customerPhone}`.replace(/\s+/g, '');
        
        if (!customerMap.has(effectiveCustomerId)) {
          customerMap.set(effectiveCustomerId, {
            id: effectiveCustomerId,
            name: customerName || 'Unknown Customer', // FIXED: Always provide a name
            phone: customerPhone,
            email: customerEmail,
            whatsapp: customerWhatsapp,
            bookings: [],
            totalSpent: 0,
            lastBooking: null,
            firstBooking: null,
            favoriteServices: new Map()
          });
        }
        
        const customer = customerMap.get(effectiveCustomerId);
        customer.bookings.push(booking);
        
        // Calculate total spent (only confirmed/rescheduled bookings)
        if (booking.status === 'confirmed' || booking.status === 'rescheduled') {
          const servicePrice = booking.servicePrice || booking.service_price || 0;
          customer.totalSpent += servicePrice;
        }
        
        // Track service preferences
        const serviceName = booking.serviceName || booking.service_name || 'Unknown Service';
        if (serviceName) {
          const serviceCount = customer.favoriteServices.get(serviceName) || 0;
          customer.favoriteServices.set(serviceName, serviceCount + 1);
        }
        
        // Track booking dates - FIXED: Handle multiple date field variations
        const bookingDate = booking.bookingDate || booking.booking_date || booking.date;
        if (bookingDate) {
          const bookingDateObj = new Date(bookingDate);
          if (!customer.lastBooking || bookingDateObj > new Date(customer.lastBooking)) {
            customer.lastBooking = bookingDate;
          }
          if (!customer.firstBooking || bookingDateObj < new Date(customer.firstBooking)) {
            customer.firstBooking = bookingDate;
          }
        }
      });
      
      // Convert to array and add calculated fields
      const customersArray = Array.from(customerMap.values()).map(customer => ({
        ...customer,
        totalBookings: customer.bookings.length,
        completedBookings: customer.bookings.filter(b => 
          b.status === 'confirmed' || b.status === 'rescheduled'
        ).length,
        cancelledBookings: customer.bookings.filter(b => b.status === 'cancelled').length,
        averageSpent: customer.completedBookings > 0 ? customer.totalSpent / customer.completedBookings : 0,
        isRepeatCustomer: customer.bookings.length > 1,
        favoriteService: customer.favoriteServices.size > 0 ? 
          Array.from(customer.favoriteServices.entries()).sort((a, b) => b[1] - a[1])[0][0] : '',
        lastBookingDays: customer.lastBooking ? 
          Math.floor((new Date() - new Date(customer.lastBooking)) / (1000 * 60 * 60 * 24)) : null
      }));
      
      setCustomers(customersArray);
      
    } catch (error) {
      setError('Failed to process customer data: ' + error.message);
      console.error('Customer processing error:', error);
    } finally {
      setLoading(false);
    }
  };

  // CRITICAL FIX: Safe filtering and sorting with null/undefined protection
  const filterCustomers = useCallback(() => {
    try {
      if (!customers || !Array.isArray(customers)) {
        setFilteredCustomers([]);
        return;
      }

      let filtered = customers.filter(customer => {
        // FIXED: Ensure customer object exists and has some identifying info
        const hasValidData = customer && (
          customer.name || 
          customer.phone || 
          customer.email
        );
        
        if (!hasValidData) {
          console.warn('Filtering out invalid customer:', customer);
          return false;
        }
        
        return true;
      });

      // Apply search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        filtered = filtered.filter(customer => {
          // FIXED: Safe string operations with fallbacks
          const customerName = (customer.name || '').toLowerCase();
          const customerPhone = (customer.phone || '').toLowerCase();
          const customerEmail = (customer.email || '').toLowerCase();
          
          return customerName.includes(search) ||
                 customerPhone.includes(search) ||
                 customerEmail.includes(search);
        });
      }

      // Apply category filter
      switch (filterBy) {
        case 'repeat':
          filtered = filtered.filter(c => c.isRepeatCustomer);
          break;
        case 'new':
          filtered = filtered.filter(c => !c.isRepeatCustomer);
          break;
        case 'high_value':
          filtered = filtered.filter(c => (c.totalSpent || 0) > 200);
          break;
        case 'recent':
          filtered = filtered.filter(c => c.lastBookingDays !== null && c.lastBookingDays <= 30);
          break;
      }

      // CRITICAL FIX: Safe sorting with null/undefined protection
      filtered.sort((a, b) => {
        try {
          switch (sortBy) {
            case 'name':
              // FIXED: Safe name comparison with fallbacks
              const nameA = a.name || a.customer_name || a.customerName || 'Unknown';
              const nameB = b.name || b.customer_name || b.customerName || 'Unknown';
              return nameA.localeCompare(nameB);
              
            case 'total_spent':
              const spentA = a.totalSpent || 0;
              const spentB = b.totalSpent || 0;
              return spentB - spentA;
              
            case 'total_bookings':
              const bookingsA = a.totalBookings || 0;
              const bookingsB = b.totalBookings || 0;
              return bookingsB - bookingsA;
              
            case 'last_booking':
              // FIXED: Safe date comparison
              if (!a.lastBooking && !b.lastBooking) return 0;
              if (!a.lastBooking) return 1;
              if (!b.lastBooking) return -1;
              
              try {
                const dateA = new Date(a.lastBooking);
                const dateB = new Date(b.lastBooking);
                return dateB - dateA;
              } catch (dateError) {
                console.warn('Date comparison error:', dateError);
                return 0;
              }
              
            default:
              // Default to safe name sorting
              const defaultNameA = a.name || 'Unknown';
              const defaultNameB = b.name || 'Unknown';
              return defaultNameA.localeCompare(defaultNameB);
          }
        } catch (sortError) {
          console.error('Sorting error:', sortError, { a, b, sortBy });
          return 0; // Neutral sort if error occurs
        }
      });

      setFilteredCustomers(filtered);
      
    } catch (error) {
      console.error('Filter customers error:', error);
      setFilteredCustomers([]);
      setError('Failed to filter customers: ' + error.message);
    }
  }, [customers, searchTerm, sortBy, filterBy]);

  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
  };

  const exportCustomers = () => {
    try {
      const csvData = filteredCustomers.map(customer => ({
        Name: customer.name || 'Unknown',
        Phone: customer.phone || '',
        Email: customer.email || '',
        'Total Bookings': customer.totalBookings || 0,
        'Total Spent': `$${(customer.totalSpent || 0).toFixed(2)}`,
        'Average Spent': `$${(customer.averageSpent || 0).toFixed(2)}`,
        'Last Booking': customer.lastBooking || 'Never',
        'Favorite Service': customer.favoriteService || 'None',
        'Customer Type': customer.isRepeatCustomer ? 'Repeat' : 'New'
      }));

      const csvContent = [
        Object.keys(csvData[0]).join(','),
        ...csvData.map(row => Object.values(row).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'customers.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export customers');
    }
  };

  const renderCustomerModal = () => {
    if (!selectedCustomer) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Customer Details</h2>
            <button
              onClick={() => setSelectedCustomer(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="font-medium text-gray-900 mb-4">Contact Information</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-600">Name:</span>
                  <span className="ml-2 font-medium">{selectedCustomer.name}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Phone:</span>
                  <span className="ml-2 font-medium">{selectedCustomer.phone}</span>
                </div>
                {selectedCustomer.email && (
                  <div>
                    <span className="text-sm text-gray-600">Email:</span>
                    <span className="ml-2 font-medium">{selectedCustomer.email}</span>
                  </div>
                )}
                <div>
                  <span className="text-sm text-gray-600">WhatsApp:</span>
                  <span className="ml-2 font-medium">{selectedCustomer.whatsapp}</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-medium text-gray-900 mb-4">Booking Statistics</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-sm text-gray-600">Total Bookings:</span>
                  <span className="ml-2 font-medium">{selectedCustomer.totalBookings}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Completed:</span>
                  <span className="ml-2 font-medium text-green-600">{selectedCustomer.completedBookings}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Cancelled:</span>
                  <span className="ml-2 font-medium text-red-600">{selectedCustomer.cancelledBookings}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Total Spent:</span>
                  <span className="ml-2 font-medium">${selectedCustomer.totalSpent.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Average per Visit:</span>
                  <span className="ml-2 font-medium">${selectedCustomer.averageSpent.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-sm text-gray-600">Favorite Service:</span>
                  <span className="ml-2 font-medium">{selectedCustomer.favoriteService || 'None'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Booking History */}
          <div>
            <h3 className="font-medium text-gray-900 mb-4">Booking History</h3>
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {selectedCustomer.bookings
                .sort((a, b) => {
                  const dateA = new Date(a.bookingDate || a.booking_date || 0);
                  const dateB = new Date(b.bookingDate || b.booking_date || 0);
                  return dateB - dateA;
                })
                .map((booking) => (
                  <div key={booking.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="font-medium">{booking.serviceName || booking.service_name || 'Unknown Service'}</div>
                      <div className="text-sm text-gray-600">
                        {new Date(booking.bookingDate || booking.booking_date).toLocaleDateString()} at {booking.time}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">${(booking.servicePrice || booking.service_price || 0).toFixed(2)}</div>
                      <div className={`text-xs px-2 py-1 rounded-full ${
                        booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                        booking.status === 'rescheduled' ? 'bg-yellow-100 text-yellow-800' :
                        booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {booking.status}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex space-x-3">
            <button className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700">
              <MessageCircle className="w-4 h-4 mr-2" />
              Send Message
            </button>
            <button className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
              <Phone className="w-4 h-4 mr-2" />
              Call
            </button>
            <button className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Processing customers...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="text-red-800">{error}</div>
        <button 
          onClick={() => {
            setError(null);
            processCustomersFromBookings();
          }}
          className="mt-2 text-red-600 hover:text-red-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Customer Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Customers</p>
              <p className="text-2xl font-bold text-gray-900">{customers.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Repeat Customers</p>
              <p className="text-2xl font-bold text-gray-900">
                {customers.filter(c => c.isRepeatCustomer).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Revenue/Customer</p>
              <p className="text-2xl font-bold text-gray-900">
                ${customers.length > 0 ? 
                  (customers.reduce((sum, c) => sum + (c.totalSpent || 0), 0) / customers.length).toFixed(0) 
                  : '0'
                }
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-orange-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Bookings</p>
              <p className="text-2xl font-bold text-gray-900">
                {customers.length > 0 ? 
                  (customers.reduce((sum, c) => sum + (c.totalBookings || 0), 0) / customers.length).toFixed(1) 
                  : '0'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="relative flex-1 min-w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Filter */}
          <div className="flex items-center">
            <Filter className="h-4 w-4 mr-2 text-gray-400" />
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Customers</option>
              <option value="repeat">Repeat Customers</option>
              <option value="new">New Customers</option>
              <option value="high_value">High Value</option>
              <option value="recent">Recent Activity</option>
            </select>
          </div>

          {/* Sort */}
          <div className="flex items-center">
            <span className="text-sm text-gray-600 mr-2">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="name">Name</option>
              <option value="total_spent">Total Spent</option>
              <option value="total_bookings">Total Bookings</option>
              <option value="last_booking">Last Booking</option>
            </select>
          </div>

          {/* Export */}
          <button
            onClick={exportCustomers}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Customers List */}
      <div className="bg-white rounded-lg shadow-sm border">
        {filteredCustomers.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm ? 'Try adjusting your search terms' : 'Your customers will appear here as they book appointments'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredCustomers.map((customer) => (
              <div 
                key={customer.id} 
                className="p-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => handleCustomerClick(customer)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h3 className="text-lg font-medium text-gray-900">{customer.name}</h3>
                      {customer.isRepeatCustomer && (
                        <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          Repeat Customer
                        </span>
                      )}
                      {(customer.totalSpent || 0) > 500 && (
                        <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                          VIP
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-600">
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 mr-2" />
                        {customer.phone}
                      </div>
                      
                      {customer.email && (
                        <div className="flex items-center">
                          <Mail className="h-4 w-4 mr-2" />
                          {customer.email}
                        </div>
                      )}
                      
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2" />
                        {customer.totalBookings} booking{customer.totalBookings !== 1 ? 's' : ''}
                      </div>
                      
                      <div className="flex items-center">
                        <DollarSign className="h-4 w-4 mr-2" />
                        ${(customer.totalSpent || 0).toFixed(2)} total
                      </div>
                    </div>
                  </div>
                  
                  <div className="ml-4">
                    <Eye className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Customer Modal */}
      {renderCustomerModal()}
    </div>
  );
};

export default CustomerManager;